import type { EntityId } from './ids.js';
import type { PrivacyClass } from './privacy.js';
import type { RecordEnvelope } from './records.js';

/**
 * Academics & Funding — the owner's identity, education history, university
 * admissions/offers, and the funding sources that pay for study.
 *
 * Money is stored as INTEGER MINOR UNITS (e.g. pence) so arithmetic is exact.
 * The funding helpers are pure and deterministic, so a dashboard can show
 * "need vs committed vs gap" and a deposit countdown without side effects.
 *
 * These records interconnect through the typed relationship graph (an Admission
 * is `part_of` a Goal; a FundingSource `supports` an Admission) — they do not
 * duplicate finance accounts or career opportunities.
 */

// ─────────────────────────── Profile ───────────────────────────

export interface EducationRecord {
  readonly institution: string;
  readonly qualification: string;
  readonly field: string;
  readonly status: 'completed' | 'in_progress' | 'planned';
  readonly endYear: number | null;
  readonly note: string;
}

/** The owner's canonical profile (one per owner). */
export interface Profile extends RecordEnvelope {
  readonly kind: 'profile';
  readonly displayName: string;
  readonly headline: string;
  readonly location: string;
  readonly education: readonly EducationRecord[];
  readonly links: readonly { readonly label: string; readonly url: string }[];
  readonly linkedIds: readonly EntityId[];
}

export interface ProfileInput {
  readonly displayName: string;
  readonly headline?: string;
  readonly location?: string;
  readonly education?: readonly EducationRecord[];
  readonly links?: readonly { readonly label: string; readonly url: string }[];
  readonly privacyClass?: PrivacyClass;
}

// ─────────────────────────── Admission ───────────────────────────

export type OfferStatus =
  | 'researching'
  | 'applied'
  | 'conditional'
  | 'unconditional'
  | 'accepted'
  | 'deferred'
  | 'declined'
  | 'rejected';
export const OFFER_STATUSES: readonly OfferStatus[] = [
  'researching', 'applied', 'conditional', 'unconditional', 'accepted', 'deferred', 'declined', 'rejected',
] as const;

export type AdmissionStatus = 'open' | 'secured' | 'withdrawn' | 'closed';
export const ADMISSION_STATUSES: readonly AdmissionStatus[] = ['open', 'secured', 'withdrawn', 'closed'] as const;

export interface Admission extends RecordEnvelope {
  readonly kind: 'admission';
  readonly institution: string;
  readonly programme: string;
  readonly offerStatus: OfferStatus;
  /** Outstanding conditions, e.g. "Pay £1,500 deposit by 30 June 2026". */
  readonly conditions: readonly string[];
  readonly currency: string;
  readonly tuitionMinor: number;
  readonly depositMinor: number;
  readonly livingEstimateMinor: number;
  readonly depositDueDate: string | null;
  readonly decisionDate: string | null;
  readonly intakeTerm: string;
  readonly status: AdmissionStatus;
  readonly note: string;
  readonly linkedIds: readonly EntityId[];
}

export interface AdmissionInput {
  readonly institution: string;
  readonly programme: string;
  readonly offerStatus?: OfferStatus;
  readonly conditions?: readonly string[];
  readonly currency?: string;
  readonly tuitionMinor?: number;
  readonly depositMinor?: number;
  readonly livingEstimateMinor?: number;
  readonly depositDueDate?: string | null;
  readonly decisionDate?: string | null;
  readonly intakeTerm?: string;
  readonly note?: string;
  readonly privacyClass?: PrivacyClass;
}

// ─────────────────────────── Funding source ───────────────────────────

export type FundingKind = 'scholarship' | 'loan' | 'family' | 'savings' | 'income' | 'grant' | 'other';
export const FUNDING_KINDS: readonly FundingKind[] = [
  'scholarship', 'loan', 'family', 'savings', 'income', 'grant', 'other',
] as const;

export type FundingStatus = 'planned' | 'open' | 'applied' | 'awarded' | 'rejected' | 'disbursed';
export const FUNDING_STATUSES: readonly FundingStatus[] = [
  'planned', 'open', 'applied', 'awarded', 'rejected', 'disbursed',
] as const;

/** How the owner reached out — so proactive pursuit is a logged by-product of doing. */
export type TouchpointChannel = 'visit' | 'call' | 'email' | 'message' | 'portal' | 'other';
export const TOUCHPOINT_CHANNELS: readonly TouchpointChannel[] = [
  'visit', 'call', 'email', 'message', 'portal', 'other',
] as const;

/** One logged contact/action against a funding source (an office visit, a call…). */
export interface Touchpoint {
  readonly on: string;
  readonly channel: TouchpointChannel;
  readonly note: string;
}

export interface FundingSource extends RecordEnvelope {
  readonly kind: 'funding_source';
  readonly name: string;
  readonly sourceKind: FundingKind;
  readonly status: FundingStatus;
  readonly currency: string;
  /** Expected/awarded amount in minor units (0 when unknown). */
  readonly amountMinor: number;
  readonly fullyFunded: boolean;
  readonly opensOn: string | null;
  readonly closesOn: string | null;
  readonly note: string;
  /** The single next thing to do to move this source forward (proactive pursuit). */
  readonly nextAction: string;
  readonly nextActionDate: string | null;
  /** History of what the owner has already done to pursue this source, newest first. */
  readonly touchpoints: readonly Touchpoint[];
  /** Linked admission/goal this funding supports (ids only). */
  readonly linkedIds: readonly EntityId[];
}

export interface FundingSourceInput {
  readonly name: string;
  readonly sourceKind?: FundingKind;
  readonly status?: FundingStatus;
  readonly currency?: string;
  readonly amountMinor?: number;
  readonly fullyFunded?: boolean;
  readonly opensOn?: string | null;
  readonly closesOn?: string | null;
  readonly note?: string;
  readonly nextAction?: string;
  readonly nextActionDate?: string | null;
  readonly privacyClass?: PrivacyClass;
}

// ─────────────────────────── Education program (completed degree) ───────────────────────────

/** One course/subject within a degree (denormalized onto the program). */
export interface Course {
  readonly code: string | null;
  readonly title: string;
  readonly category: string | null;
  readonly typeLabel: string | null;
  readonly credits: number | null;
  /** Lecture-Tutorial-Practical-Project hours, e.g. "3-0-2-0". */
  readonly ltpj: string | null;
  readonly instructor: string | null;
  readonly term: string | null;
  readonly result: string | null;
}

/** Credits earned in one university course-category bucket. */
export interface CreditLine {
  readonly category: string;
  readonly credits: number;
}

/**
 * A whole degree/program the owner studied, with its courses and credit
 * breakdown embedded. Denormalized on purpose: this is historical, read-mostly
 * data for a single owner, so one record per degree keeps it simple and calm.
 * Migrated faithfully from v1's Academic* hierarchy.
 */
export interface EducationProgram extends RecordEnvelope {
  readonly kind: 'education_program';
  readonly institution: string;
  readonly qualification: string;
  readonly title: string;
  readonly field: string | null;
  readonly branch: string | null;
  readonly school: string | null;
  readonly registrationNumber: string | null;
  readonly institutionEmail: string | null;
  readonly city: string | null;
  readonly country: string | null;
  readonly website: string | null;
  readonly startDate: string | null;
  readonly endDate: string | null;
  readonly totalCredits: number | null;
  readonly cgpa: number | null;
  readonly gradeLabel: string | null;
  readonly status: 'in_progress' | 'completed' | 'planned';
  readonly creditSummary: readonly CreditLine[];
  readonly courses: readonly Course[];
  readonly linkedIds: readonly EntityId[];
}

export interface EducationProgramInput {
  readonly institution: string;
  readonly qualification: string;
  readonly title: string;
  readonly field?: string | null;
  readonly branch?: string | null;
  readonly school?: string | null;
  readonly registrationNumber?: string | null;
  readonly institutionEmail?: string | null;
  readonly city?: string | null;
  readonly country?: string | null;
  readonly website?: string | null;
  readonly startDate?: string | null;
  readonly endDate?: string | null;
  readonly totalCredits?: number | null;
  readonly cgpa?: number | null;
  readonly gradeLabel?: string | null;
  readonly status?: 'in_progress' | 'completed' | 'planned';
  readonly creditSummary?: readonly CreditLine[];
  readonly courses?: readonly Course[];
  readonly privacyClass?: PrivacyClass;
}

/** Total credits across a program's course list. Pure. */
export function totalCourseCredits(courses: readonly Course[]): number {
  return courses.reduce((n, c) => n + (c.credits ?? 0), 0);
}

/** Distinct human-readable category labels in display order. Pure. */
export function creditSummaryTotal(lines: readonly CreditLine[]): number {
  return lines.reduce((n, l) => n + (l.credits ?? 0), 0);
}

// ─────────────────────────── Pure helpers ───────────────────────────

const trunc = (n: number): number => Math.max(0, Math.trunc(n));

/** Total money needed for a year of study = tuition + estimated living. Pure. */
export function studyFundingNeedMinor(a: Pick<Admission, 'tuitionMinor' | 'livingEstimateMinor'>): number {
  return trunc(a.tuitionMinor) + trunc(a.livingEstimateMinor);
}

const SECURED_STATES: readonly FundingStatus[] = ['awarded', 'disbursed'];
const PIPELINE_STATES: readonly FundingStatus[] = ['planned', 'open', 'applied'];

/** Money actually secured (awarded/disbursed). Pure. */
export function committedFundingMinor(sources: readonly Pick<FundingSource, 'status' | 'amountMinor'>[]): number {
  return sources.filter((s) => SECURED_STATES.includes(s.status)).reduce((n, s) => n + trunc(s.amountMinor), 0);
}

/** Money in the pipeline (planned/open/applied) — hoped for, not yet secured. Pure. */
export function pipelineFundingMinor(sources: readonly Pick<FundingSource, 'status' | 'amountMinor'>[]): number {
  return sources.filter((s) => PIPELINE_STATES.includes(s.status)).reduce((n, s) => n + trunc(s.amountMinor), 0);
}

/** Remaining gap after committed funding. Never negative. Pure. */
export function fundingGapMinor(needMinor: number, committedMinor: number): number {
  return Math.max(0, trunc(needMinor) - trunc(committedMinor));
}

/** Whole days from now until a target date (negative = overdue). Pure. */
export function daysUntil(targetIso: string, nowIso: string): number {
  const target = Date.parse(`${targetIso.slice(0, 10)}T00:00:00Z`);
  const now = Date.parse(`${nowIso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(target) || Number.isNaN(now)) return 0;
  return Math.round((target - now) / 86_400_000);
}


// ─────────────────────────── Deadlines (cross-surface) ───────────────────────────

export type DeadlineKind = 'deposit' | 'decision';

export interface DeadlineItem {
  readonly admissionId: EntityId;
  readonly label: string;
  readonly dueDate: string;
  readonly daysLeft: number;
  readonly kind: DeadlineKind;
}

/**
 * Collect dated, still-relevant admission deadlines (deposit + decision),
 * soonest first. Pure — so Today and the assistant can surface the same list.
 * Closed/withdrawn admissions and secured deposits are excluded.
 */
export function admissionDeadlines(
  admissions: readonly Admission[],
  nowIso: string,
): readonly DeadlineItem[] {
  const out: DeadlineItem[] = [];
  for (const a of admissions) {
    if (a.status === 'withdrawn' || a.status === 'closed') continue;
    const where = `${a.institution} — ${a.programme}`;
    // Deposit deadline only matters until the place is secured.
    if (a.depositDueDate && a.status !== 'secured') {
      out.push({
        admissionId: a.id,
        label: `Pay deposit · ${where}`,
        dueDate: a.depositDueDate,
        daysLeft: daysUntil(a.depositDueDate, nowIso),
        kind: 'deposit',
      });
    }
    if (a.decisionDate) {
      out.push({
        admissionId: a.id,
        label: `Decision · ${where}`,
        dueDate: a.decisionDate,
        daysLeft: daysUntil(a.decisionDate, nowIso),
        kind: 'decision',
      });
    }
  }
  return out.sort((x, y) => x.daysLeft - y.daysLeft);
}


// ─────────────────────────── Funding follow-ups (proactive pursuit) ───────────────────────────

export interface FollowUpItem {
  readonly fundingId: EntityId;
  readonly name: string;
  readonly action: string;
  readonly dueDate: string;
  readonly daysLeft: number;
}

/** A funding source is "live" while it can still move forward (not rejected/disbursed). */
const TERMINAL_FUNDING: readonly FundingStatus[] = ['rejected', 'disbursed'];

/**
 * Collect dated next-actions on live funding sources, soonest first. Pure — so
 * Today and the assistant surface the same proactive nudges the owner set for
 * himself (e.g. "Confirm scholarship application open date"). Sources with no
 * next-action date, or terminal status, are excluded.
 */
export function fundingFollowUps(
  sources: readonly FundingSource[],
  nowIso: string,
): readonly FollowUpItem[] {
  const out: FollowUpItem[] = [];
  for (const s of sources) {
    if (TERMINAL_FUNDING.includes(s.status)) continue;
    if (!s.nextActionDate || s.nextAction.trim().length === 0) continue;
    out.push({
      fundingId: s.id,
      name: s.name,
      action: s.nextAction,
      dueDate: s.nextActionDate,
      daysLeft: daysUntil(s.nextActionDate, nowIso),
    });
  }
  return out.sort((x, y) => x.daysLeft - y.daysLeft);
}